import { useEffect, useRef, useState } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import SearchResults from './SearchResults';

export default function GlobalSearch() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    query,
    setQuery,
    results,
    isLoading,
    error,
    recentSearches,
  } = useGlobalSearch();

  // Handle keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        inputRef.current?.focus();
      }

      // ESC to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, setQuery]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setQuery('');
  };

  const handleRecentSearchClick = (searchQuery: string) => {
    setQuery(searchQuery);
    inputRef.current?.focus();
  };

  const showDropdown = isOpen && (query.length >= 2 || recentSearches.length > 0);

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Search Input */}
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
          <Search className="w-5 h-5 text-base-content/40 group-focus-within:text-primary transition-colors z-50" />
        </div>

        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={t('search.placeholder')}
          className="input input-bordered w-full pl-12 pr-24 h-11 bg-base-200 focus:bg-base-100 transition-colors shadow-sm focus:shadow-md"
        />

        <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-2">
          {/* Loading indicator or Clear button */}
          {isLoading && (
            <Loader2 className="w-4 h-4 text-base-content/40 animate-spin" />
          )}
          {!isLoading && query && (
            <button
              onClick={() => setQuery('')}
              className="p-1 hover:bg-base-300 rounded transition-colors"
              aria-label={t('search.clear')}
            >
              <X className="w-4 h-4 text-base-content/60" />
            </button>
          )}

          {/* Keyboard hint */}
          {!isOpen && !query && (
            <div className="hidden lg:flex items-center gap-1 px-2 py-1 bg-base-300 rounded text-xs font-medium text-base-content/60">
              <kbd className="kbd kbd-xs">⌘</kbd>
              <kbd className="kbd kbd-xs">K</kbd>
            </div>
          )}
        </div>
      </div>

      {/* Dropdown Results */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-base-100 rounded-lg shadow-xl border border-base-300 z-[60] overflow-hidden">
          {error && (
            <div className="p-4 text-sm text-error bg-error/10">
              {error}
            </div>
          )}

          {!error && results && (
            <SearchResults
              results={results.results}
              query={query}
              recentSearches={recentSearches}
              onClose={handleClose}
              onRecentSearchClick={handleRecentSearchClick}
            />
          )}

          {!error && !results && (
            <SearchResults
              results={[]}
              query={query}
              recentSearches={recentSearches}
              onClose={handleClose}
              onRecentSearchClick={handleRecentSearchClick}
            />
          )}
        </div>
      )}
    </div>
  );
}
